import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useDarkTheme } from 'core/hooks/useDarkTheme'
import { useDebugStatistiquesQuery } from 'iadocs/api/debug'
import { getDebugErrorMessage } from 'iadocs/debug/display'
import { useDebugHeader } from 'iadocs/debug/search'
import {
  StatsPreset,
  granularityFor,
  isIsoDate,
  periodSuffix,
  presetRange,
  readPreset,
  shiftPresetRange,
} from 'iadocs/debug/statsPeriod'
import { DebugStatistiqueBucket } from 'iadocs/interfaces/IDebug'

const CHART_FONT = 'Lexend, Arial, sans-serif'

const chartTheme = (isDark: boolean) => {
  const ink = isDark ? '#d9d9d9' : '#4d5154'
  const grid = isDark ? '#4d5154' : '#eaebee'
  return {
    tick: { fontSize: 12, fill: ink, fontFamily: CHART_FONT },
    grid,
    tooltip: {
      contentStyle: {
        background: isDark ? '#1c1c1c' : '#ffffff',
        border: `1px solid ${grid}`,
        borderRadius: 10,
        color: ink,
        fontFamily: CHART_FONT,
        fontSize: 13,
      },
      labelStyle: { color: isDark ? '#7bb4ff' : '#0084fb' },
      itemStyle: { color: ink },
    },
    legend: {
      color: ink,
      fontFamily: CHART_FONT,
      fontSize: 13,
    },
  }
}

const SERIES = [
  { key: 'total', label: 'Total', color: '#0084fb' },
  { key: 'aboutie', label: 'Aboutie', color: '#00a8b3' },
  { key: 'echec', label: 'Échec', color: '#fe434c' },
  { key: 'rejetee', label: 'Rejetée', color: '#678097' },
] as const

const PRESETS: {
  id: Exclude<StatsPreset, 'custom'>
  label: string
  previous: string
  next: string
}[] = [
  {
    id: 'week',
    label: 'Semaine',
    previous: 'Semaine précédente',
    next: 'Semaine suivante',
  },
  {
    id: 'month',
    label: 'Mois',
    previous: 'Mois précédent',
    next: 'Mois suivant',
  },
  {
    id: 'year',
    label: 'Année',
    previous: 'Année précédente',
    next: 'Année suivante',
  },
]

const formatBucket = (bucket: string) => {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bucket)
  if (day) {
    return `${day[3]}/${day[2]}/${day[1]}`
  }
  const week = /^(\d{4})-W(\d{2})$/.exec(bucket)
  if (week) {
    return `S${week[2]} ${week[1]}`
  }
  const month = /^(\d{4})-(\d{2})$/.exec(bucket)
  if (month) {
    return `${month[2]}/${month[1]}`
  }
  return bucket
}

const exportCsv = (
  from: string,
  to: string,
  series: DebugStatistiqueBucket[]
) => {
  const lines = [
    ['periode', 'total', 'aboutie', 'echec', 'rejetee'].join(';'),
    ...series.map((row) =>
      [row.bucket, row.total, row.aboutie, row.echec, row.rejetee].join(';')
    ),
  ]
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], {
    type: 'text/csv;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `statistiques-iadocs-${from}_${to}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export const DebugStatistiquesPage = () => {
  useDebugHeader()
  const { isDarkTheme } = useDarkTheme()
  const theme = chartTheme(isDarkTheme)
  const [searchParams, setSearchParams] = useSearchParams()
  const [draftFrom, setDraftFrom] = useState('')
  const [draftTo, setDraftTo] = useState('')
  const [dateError, setDateError] = useState<string | null>(null)

  const applied = useMemo(() => {
    const preset = readPreset(searchParams.get('preset'))
    const from = searchParams.get('from') ?? ''
    const to = searchParams.get('to') ?? ''
    const hasRange = isIsoDate(from) && isIsoDate(to) && from <= to
    if (!hasRange) {
      const fallback = preset === 'custom' ? 'month' : preset
      const range = presetRange(fallback)
      return {
        preset: fallback,
        ...range,
        granularity: granularityFor(fallback, range.from, range.to),
      }
    }
    return {
      preset,
      from,
      to,
      granularity: granularityFor(preset, from, to),
    }
  }, [searchParams])

  useEffect(() => {
    setDraftFrom(applied.from)
    setDraftTo(applied.to)
    setDateError(null)
  }, [applied.from, applied.to])

  const { data, isLoading, isFetching, isError, error } =
    useDebugStatistiquesQuery({
      from: applied.from,
      to: applied.to,
      granularity: applied.granularity,
    })

  const writeRange = (
    preset: Exclude<StatsPreset, 'custom'>,
    range: { from: string; to: string }
  ) => {
    const params = new URLSearchParams()
    params.set('preset', preset)
    params.set('from', range.from)
    params.set('to', range.to)
    setSearchParams(params)
  }

  const applyPreset = (preset: Exclude<StatsPreset, 'custom'>) => {
    writeRange(preset, presetRange(preset))
  }

  const shiftPreset = (
    preset: Exclude<StatsPreset, 'custom'>,
    delta: -1 | 1
  ) => {
    const base =
      applied.preset === preset ? applied.from : presetRange(preset).from
    writeRange(preset, shiftPresetRange(preset, base, delta))
  }

  const canGoNext = (preset: Exclude<StatsPreset, 'custom'>) => {
    const base =
      applied.preset === preset ? applied.from : presetRange(preset).from
    return base < presetRange(preset).from
  }

  const onCustomPeriod = (event: FormEvent) => {
    event.preventDefault()
    if (!isIsoDate(draftFrom) || !isIsoDate(draftTo) || draftFrom > draftTo) {
      setDateError(
        'La date de début doit être antérieure ou égale à la date de fin.'
      )
      return
    }
    setDateError(null)
    const params = new URLSearchParams()
    params.set('preset', 'custom')
    params.set('from', draftFrom)
    params.set('to', draftTo)
    setSearchParams(params)
  }

  const chartData = (data?.series ?? []).map((row) => ({
    ...row,
    label: formatBucket(row.bucket),
  }))
  const suffix = periodSuffix(applied.preset, applied.from)

  return (
    <div className="iadocs-debug">
      <section className="debug-card">
        <header className="debug-header">
          <div>
            <h1>Statistiques</h1>
            <p className="debug-meta">
              Envois du {formatBucket(applied.from)} au{' '}
              {formatBucket(applied.to)}
              {suffix ? ` (${suffix})` : ''}
              {isFetching ? ' · chargement…' : ''}
            </p>
          </div>
          <button
            type="button"
            className="debug-btn"
            disabled={!data || data.series.length === 0}
            onClick={() => {
              if (data) {
                exportCsv(data.from, data.to, data.series)
              }
            }}
          >
            Exporter CSV
          </button>
        </header>

        <form
          className="debug-filters debug-filters-stats"
          onSubmit={onCustomPeriod}
        >
          <div className="debug-preset-row" role="group" aria-label="Période">
            {PRESETS.map((preset) => (
              <div key={preset.id} className="debug-preset-nav">
                <button
                  type="button"
                  className="debug-btn debug-btn-arrow"
                  aria-label={preset.previous}
                  onClick={() => shiftPreset(preset.id, -1)}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={`debug-btn${applied.preset === preset.id ? ' debug-btn-primary' : ''}`}
                  onClick={() => applyPreset(preset.id)}
                >
                  {preset.label}
                </button>
                <button
                  type="button"
                  className="debug-btn debug-btn-arrow"
                  aria-label={preset.next}
                  disabled={!canGoNext(preset.id)}
                  onClick={() => shiftPreset(preset.id, 1)}
                >
                  ›
                </button>
              </div>
            ))}
          </div>
          <label className="debug-field">
            <span className="debug-label">Date de début</span>
            <input
              className="debug-input"
              type="date"
              value={draftFrom}
              onChange={(event) => setDraftFrom(event.target.value)}
            />
          </label>
          <label className="debug-field">
            <span className="debug-label">Date de fin</span>
            <input
              className="debug-input"
              type="date"
              value={draftTo}
              onChange={(event) => setDraftTo(event.target.value)}
            />
          </label>
          <div className="debug-filters-actions">
            <button type="submit" className="debug-btn debug-btn-primary">
              Filtrer
            </button>
          </div>
        </form>
        {dateError ? <p className="debug-flash error">{dateError}</p> : null}
        {isError ? (
          <p className="debug-flash error">{getDebugErrorMessage(error)}</p>
        ) : null}

        <div className="debug-stats-totals">
          {SERIES.map((serie) => (
            <article key={serie.key} className="debug-stat-card">
              <span className="debug-label">{serie.label}</span>
              <strong>
                {data?.totals[serie.key] ?? (isLoading ? '…' : 0)}
              </strong>
            </article>
          ))}
        </div>

        <div className="debug-charts">
          <section>
            <h2>Courbes</h2>
            <div className="debug-chart">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis
                    dataKey="label"
                    tick={theme.tick}
                    axisLine={{ stroke: theme.grid }}
                    tickLine={{ stroke: theme.grid }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={theme.tick}
                    axisLine={{ stroke: theme.grid }}
                    tickLine={{ stroke: theme.grid }}
                  />
                  <Tooltip {...theme.tooltip} />
                  <Legend wrapperStyle={theme.legend} />
                  {SERIES.map((serie) => (
                    <Line
                      key={serie.key}
                      type="monotone"
                      dataKey={serie.key}
                      name={serie.label}
                      stroke={serie.color}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </section>
          <section>
            <h2>Histogramme</h2>
            <div className="debug-chart">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} />
                  <XAxis
                    dataKey="label"
                    tick={theme.tick}
                    axisLine={{ stroke: theme.grid }}
                    tickLine={{ stroke: theme.grid }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={theme.tick}
                    axisLine={{ stroke: theme.grid }}
                    tickLine={{ stroke: theme.grid }}
                  />
                  <Tooltip {...theme.tooltip} />
                  <Legend wrapperStyle={theme.legend} />
                  {SERIES.map((serie) => (
                    <Bar
                      key={serie.key}
                      dataKey={serie.key}
                      name={serie.label}
                      fill={serie.color}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        </div>

        <div className="debug-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Période</th>
                <th className="debug-num">Total</th>
                <th className="debug-num">Aboutie</th>
                <th className="debug-num">Échec</th>
                <th className="debug-num">Rejetée</th>
              </tr>
            </thead>
            <tbody>
              {chartData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="debug-empty">
                    {isLoading ? 'Chargement…' : 'Aucune période à afficher.'}
                  </td>
                </tr>
              ) : (
                chartData.map((row) => (
                  <tr key={row.bucket}>
                    <td>{row.label}</td>
                    <td className="debug-num">{row.total}</td>
                    <td className="debug-num">{row.aboutie}</td>
                    <td className="debug-num">{row.echec}</td>
                    <td className="debug-num">{row.rejetee}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
